import { usePage } from "@inertiajs/react"
import PhotoCrossfade from "@/components/PhotoCrossfade"
import { HeroFilm, type HeroPhoto } from "@/components/gisela/HeroBand"
import { useLocalePath } from "@/lib/locale-path"
import type { PageProps, PlacementPhoto } from "@/types/inertia"

interface Props {
  slides: PlacementPhoto[]
  /** How long each photo holds, from the section's display settings. */
  intervalMs?: number
  /** The photograph the whole band sits on, under its film. nil = plain blue. */
  background?: HeroPhoto | null
  content: { intro_lead?: string; intro_sub?: string }
  therapistName: string
}

// Homepage hero: welcome text left (~35%), one big photo circle right (~65%)
// running off the right edge, with a same-size outline circle nudged up and
// left behind it so a thin line shows around part of the photo. The band sits
// on the "Hero background" photo under its film — the same ground every other
// hero on her site uses (HeroBand), so white copy reads on any picture and a
// page with no background chosen still looks intentional on plain blue.
// Photos are admin-placed: "therapy.hero" fills the circle, and with none
// placed the circle renders as a sage colour field.
export default function GiselaHomeHero({ slides, intervalMs, background, content, therapistName }: Props) {
  const { props } = usePage<PageProps>()
  const t = props.translations.gisela_pages.hero
  const tn = props.translations.gisela_nav
  const tc = props.translations.gisela_pages.common
  const lp = useLocalePath()
  const onGiselaHost = props.current_brand.key === "gisela"
  // First name only, to keep the heading short enough that the "Book a free
  // consultation" button stays visible without scrolling — the full name
  // still appears everywhere else (meta tags, footer, emails).
  const firstName = therapistName.split(" ")[0]

  // The full headline tier of her conditions menu, in menu order — the hero
  // list and the nav dropdown say the same thing.
  const conditionLinks = props.gisela_condition_menu?.[0]?.items ?? []

  return (
    <section className="relative overflow-hidden bg-teal pt-16 text-white">
      <HeroFilm photo={background} />
      <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-6 pt-6 pb-14 lg:grid-cols-12 lg:gap-6 lg:px-8 lg:pt-16 lg:pb-20">
        {/* Text column — below the photo on mobile, beside it from lg up. It
            sits to the top of the row rather than centred against the taller
            circle, so the whole column down to the CTAs fits on screen. */}
        <div className="relative z-10 order-2 lg:order-1 lg:col-span-5 lg:-mt-6 lg:self-start">
          <p className="text-2xl italic text-teal-light" style={{ fontFamily: "var(--font-display)" }}>
            {t.welcome}
          </p>
          <h1 className="text-hero mt-2 text-white">
            {t.headline_prefix} <span className="text-teal-light">{firstName}</span>
          </h1>
          {content.intro_lead && (
            <p className="mt-5 text-lg font-semibold leading-relaxed text-white">{content.intro_lead}</p>
          )}
          {content.intro_sub && <p className="mt-4 text-white/70">{content.intro_sub}</p>}

          {onGiselaHost && conditionLinks.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-medium text-white">{t.help_with_label}</p>
              <ul className="mt-3 space-y-1.5">
                {conditionLinks.map(({ label, path }) => (
                  <li key={path} className="flex items-center gap-2.5">
                    <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-sage" />
                    <a href={lp(path)} className="text-white underline underline-offset-4 hover:text-white/80">
                      {label}
                    </a>
                  </li>
                ))}
                <li className="flex items-center gap-2.5">
                  <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-sage" />
                  <a
                    href={lp("/what-i-can-help-with")}
                    className="font-medium text-white underline underline-offset-4 hover:text-white/80"
                  >
                    {t.see_all_help}
                  </a>
                </li>
              </ul>
            </div>
          )}

          {/* One way in on her site — the free consultation; the enquiry form
              is a nav click away. Off her host (the venue's copy of this
              hero) the enquiry link is the only call to action. The button is
              white on the film rather than teal-on-cream: a navy pill on a
              navy film would not be a button at all. */}
          <div className="mt-6 flex flex-wrap items-center gap-4">
            {onGiselaHost ? (
              <a
                href={lp("/book")}
                className="rounded-full bg-white px-6 py-2.5 text-sm font-medium text-teal no-underline shadow transition-opacity hover:opacity-90"
              >
                {tn.book_call}
              </a>
            ) : (
              <a href="#enquiry-form" className="text-sm font-medium text-white underline underline-offset-4">
                {tc.send_enquiry}
              </a>
            )}
          </div>
        </div>

        {/* Photo circle — first thing on a phone. From lg it is a fixed disc
            (the canvas's 700px on a 1440px artboard plus a fifth, at Joe's request; a step smaller at lg)
            sat at the end of its column and pushed 200px past the container's
            right edge, so the viewport clips a sliver of it. A fixed size
            rather than a column-filling one: the column is wider than the
            disc on big screens, and the disc must not grow with it. */}
        <div className="relative order-1 mx-auto aspect-square w-full max-w-[420px] sm:max-w-[520px] lg:order-2 lg:col-span-7 lg:mx-0 lg:mt-3 lg:-mr-[200px] lg:h-[720px] lg:w-[720px] lg:max-w-none lg:justify-self-end xl:h-[840px] xl:w-[840px]">
          {/* The line partly around it: the same circle offset up and left, so
              only a crescent of it clears the photo. Offsets are the canvas's
              40px/30px on an 880px circle, kept proportional. The stone circle
              that used to echo it on the far side is gone with the cream: a
              soft tonal note on cream is an opaque blob on a photograph. */}
          <div
            aria-hidden
            className="absolute -top-[3.4%] -left-[4.5%] h-full w-full rounded-full border-2 border-white/55"
          />
          <div className="relative h-full w-full overflow-hidden rounded-full shadow-lg">
            {slides.length > 0 ? (
              <PhotoCrossfade
                slides={slides}
                intervalMs={intervalMs}
                className="absolute inset-0"
                dotLabel={n => t.go_to_slide.replace("%{number}", String(n))}
              />
            ) : (
              <div className="h-full w-full bg-sage-light" />
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
